import React from 'react';
import {
    MessageSquare,
    Target,
    FileText,
    Calendar,
    Star,
    AlertCircle,
    CheckCircle2,
    Shield,
    Ban,
    Zap,
    Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const TimelineFeed = ({ staff, isEditing, onDeleteNote, onDeleteAlignment, onDeleteConduct }) => {

    // 1. Convert varying data sources into a unified "Feed Item" format
    const getFeedItems = () => {
        const items = [];

        // Admin Notes
        if (staff?.staffDetails?.adminNotes) {
            staff.staffDetails.adminNotes.forEach(note => {
                let icon = FileText;
                let color = 'purple';

                switch (note.category) {
                    case 'Elogio':
                        icon = Star;
                        color = 'emerald';
                        break;
                    case 'Feedback':
                        icon = MessageSquare;
                        color = 'blue';
                        break;
                    case 'Treinamento':
                        icon = Zap;
                        color = 'blue';
                        break;
                    case 'Disciplinar':
                        icon = Shield;
                        color = 'red';
                        break;
                    case 'Advertência Verbal':
                        icon = AlertCircle;
                        color = 'amber';
                        break;
                    case 'Advertência Escrita':
                    case 'Suspensão':
                        icon = Ban;
                        color = 'red';
                        break;
                }

                items.push({
                    id: `note-${note.id}`,
                    originalId: note.id,
                    type: 'note',
                    date: new Date(note.date),
                    title: note.title,
                    content: note.description,
                    category: note.category,
                    icon,
                    color
                });
            });
        }

        // Alignments
        if (staff?.staffDetails?.alignments) {
            staff.staffDetails.alignments.forEach(align => {
                items.push({
                    id: `align-${align.id}`,
                    originalId: align.id,
                    type: 'alignment',
                    date: new Date(align.date),
                    title: align.topic,
                    content: align.actions,
                    status: align.status,
                    icon: Target,
                    color: align.status === 'Resolved' ? 'emerald' : 'amber'
                });
            });
        }

        // Monthly Feedback (Usually stored by month name, might need mapping if date not present)
        // Assuming editForm structure from parent or saved structure
        if (staff?.staffDetails?.monthlyFeedback) {
            staff.staffDetails.monthlyFeedback.forEach((fb, idx) => {
                // If updatedAt exists use it, otherwise approximation based on month string might be needed
                // For now, if no date, we push it to 1st of that month or just end of list
                const date = fb.updatedAt ? new Date(fb.updatedAt) : new Date();
                items.push({
                    id: `fb-${idx}`,
                    type: 'feedback',
                    date: date,
                    title: `Feedback: ${fb.month}`,
                    content: fb.content,
                    icon: MessageSquare,
                    color: 'blue'
                });
            });
        }

        // Conduct History (Automatic)
        if (staff?.staffDetails?.conductHistory) {
            staff.staffDetails.conductHistory.forEach(history => {
                items.push({
                    id: `conduct-${history.id}`,
                    originalId: history.id,
                    type: 'conduct',
                    date: new Date(history.date),
                    title: history.title,
                    content: history.description,
                    icon: AlertCircle,
                    color: history.newStatus === 'red' ? 'red' :
                        (history.newStatus === 'orange' || history.newStatus === 'yellow') ? 'amber' :
                            history.newStatus === 'green' ? 'emerald' :
                                history.newStatus === 'purple' ? 'purple' : 'blue'
                });
            });
        }

        // Contract Start (System Event)
        if (staff?.staffDetails?.startDate) {
            items.push({
                id: 'sys-start',
                type: 'system',
                date: new Date(staff.staffDetails.startDate),
                title: 'Início do Contrato',
                content: 'Data de contratação oficial.',
                icon: Calendar,
                color: 'zinc'
            });
        }

        // Sort by Date Descending
        return items.sort((a, b) => b.date - a.date);
    };

    const feedItems = getFeedItems();

    const getColorClasses = (color) => {
        const map = {
            purple: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20', dot: 'bg-purple-500', tag: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
            emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', dot: 'bg-emerald-500', tag: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
            amber: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', dot: 'bg-amber-500', tag: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
            blue: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', dot: 'bg-blue-500', tag: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
            red: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', dot: 'bg-red-500', tag: 'bg-red-500/20 text-red-300 border-red-500/30' },
            zinc: { bg: 'bg-zinc-500/10', text: 'text-zinc-400', border: 'border-zinc-500/20', dot: 'bg-zinc-500', tag: 'bg-zinc-800 text-zinc-400 border-zinc-700' },
        };
        return map[color] || map.zinc;
    };

    const handleDelete = (item) => {
        if (!window.confirm('Deseja realmente excluir esta entrada da timeline?')) return;

        if (item.type === 'note') onDeleteNote(item.originalId);
        if (item.type === 'alignment') onDeleteAlignment(item.originalId);
        if (item.type === 'conduct') onDeleteConduct(item.originalId);
    };

    if (feedItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-zinc-500 border border-dashed border-white/5 rounded-2xl">
                <div className="w-12 h-12 rounded-full bg-zinc-900 flex items-center justify-center mb-3">
                    <Calendar className="w-6 h-6 opacity-50" />
                </div>
                <p className="text-sm font-medium">Cronograma vazio</p>
                <p className="text-xs opacity-60">Nenhuma atividade registrada.</p>
            </div>
        );
    }

    return (
        <div className="relative space-y-8 pl-6 before:absolute before:left-[11px] before:top-2 before:bottom-0 before:w-[2px] before:bg-zinc-800">
            {feedItems.map((item) => {
                const style = getColorClasses(item.color);
                const Icon = item.icon;

                return (
                    <div key={item.id} className="relative animate-in slide-in-from-left-4 duration-500">
                        {/* Timeline Dot */}
                        <div className={`absolute -left-[29px] w-6 h-6 rounded-full border-4 border-[#09090b] ${style.dot} flex items-center justify-center`}>
                        </div>

                        {/* Card */}
                        <div className={`rounded-xl border ${style.border} ${style.bg} p-4 relative group hover:bg-opacity-20 transition-all`}>
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                    <Icon className={`w-4 h-4 ${style.text}`} />
                                    <h4 className={`text-sm font-bold text-white`}>{item.title}</h4>
                                    {item.category && (
                                        <span className={`text-[9px] uppercase px-2 py-0.5 rounded-full font-bold border ${style.tag}`}>
                                            {item.category}
                                        </span>
                                    )}
                                    {item.type === 'alignment' && (
                                        <span className={`text-[9px] uppercase px-2 py-0.5 rounded-full font-bold border ${style.tag}`}>
                                            Alinhamento
                                        </span>
                                    )}
                                    {item.type === 'conduct' && (
                                        <span className={`text-[9px] uppercase px-2 py-0.5 rounded-full font-bold border ${style.tag}`}>
                                            Conduta
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-[10px] text-zinc-500 font-mono">
                                        {format(item.date, "d MMM yyyy", { locale: ptBR })}
                                    </span>
                                    {item.type !== 'system' && (
                                        <button
                                            onClick={() => handleDelete(item)}
                                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-all"
                                            title="Excluir entrada"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
                                {item.content}
                            </p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default TimelineFeed;
